"use client";

import React, { useState, useRef, useEffect } from "react";
import { Wand, Copy, Check, CircleAlert, Loader } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function AiTextSummarizer() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [summaryLength, setSummaryLength] = useState("medium");
  const [summaryStyle, setSummaryStyle] = useState("paragraph");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const generateSummary = async (text: string, length: string, style: string) => {
    return new Promise<string>((resolve, reject) => {
      setTimeout(() => {
        if (requestCount > 3) {
          reject(new Error("Rate limit exceeded. Please wait a moment before trying again."));
          return;
        }
        if (text.trim().length < 30) {
          reject(new Error("Text is too short. Please paste at least 30 characters to generate a meaningful summary."));
          return;
        }
        let result = "";
        const prefix = length === "short" ? "Briefly, " : length === "long" ? "In detail, " : "In summary, ";
        if (style === "bullets") {
          result = `${prefix}the text covers the following key points:\n\n`;
          result += `• The primary subject matter revolves around the core concepts mentioned in your input.\n`;
          result += `• Significant emphasis is placed on the development and implications of these ideas.\n`;
          if (length === "medium" || length === "long") {
            result += `• Various supporting arguments are presented to validate the main thesis.\n`;
          }
          if (length === "long") {
            result += `• The conclusion synthesizes these elements into a cohesive final thought.\n`;
            result += `• Further considerations are suggested for future exploration.\n`;
          }
        } else {
          result = `${prefix}the provided text discusses the core concepts and their implications. It highlights the main ideas and provides a concise overview of the subject matter. `;
          if (length === "medium" || length === "long") {
            result += `The author presents various supporting arguments to validate the main thesis, ensuring a comprehensive understanding of the topic. `;
          }
          if (length === "long") {
            result += `Ultimately, the conclusion synthesizes these elements into a cohesive final thought, suggesting further considerations for future exploration and study.`;
          }
        }
        resolve(result);
      }, 1500 + Math.random() * 1000);
    });
  };
  const handleSummarize = async () => {
    if (!inputText.trim()) return;
    setIsLoading(true);
    setError(null);
    setIsSuccess(false);
    setOutputText("");
    setRequestCount(prev => prev + 1);
    try {
      const summary = await generateSummary(inputText, summaryLength, summaryStyle);
      setOutputText(summary);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred while generating the summary.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleCopy = async () => {
    if (!outputText) return;
    try {
      await navigator.clipboard.writeText(outputText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };
  useEffect(() => {
    if (isSuccess) setIsSuccess(false);
    if (error) setError(null);
  }, [inputText, summaryLength, summaryStyle]);
  return;
}